/**
 * Geolocation Utilities
 *
 * GPS → IP fallback 위치 조회를 통합한 유틸리티.
 * BottomNavBar, SearchBar, SearchLocationPicker, useCategoryNavigation에서 공통 사용.
 */

import { getLocationFromIP } from "@/lib/cities";

export interface GeoCoords {
  latitude: number;
  longitude: number;
}

export interface GeoBounds {
  ne_lat: number;
  ne_lng: number;
  sw_lat: number;
  sw_lng: number;
}

/** 내 주변 검색용 기본 offset (±0.03도 ≈ 3km) */
const NEARBY_OFFSET = 0.03;

/**
 * 현재 위치를 가져옵니다 (GPS → IP fallback).
 *
 * @returns 위치 좌표 또는 null (모든 방법 실패 시)
 */
export async function getCurrentLocation(): Promise<GeoCoords | null> {
  // GPS 시도
  const gpsResult = await getGPSLocation().catch(() => null);
  if (gpsResult) return gpsResult;

  // IP fallback
  const ipResult = await getIPLocation().catch(() => null);
  return ipResult;
}

/**
 * 현재 위치 기반 검색 bounds를 계산합니다.
 *
 * @returns bounds 또는 null
 */
export async function getNearbyBounds(): Promise<GeoBounds | null> {
  const coords = await getCurrentLocation();
  if (!coords) return null;
  return toBounds(coords);
}

/**
 * 좌표를 bounds로 변환합니다.
 */
export function toBounds(
  coords: GeoCoords,
  offset: number = NEARBY_OFFSET,
): GeoBounds {
  return {
    ne_lat: coords.latitude + offset,
    ne_lng: coords.longitude + offset,
    sw_lat: coords.latitude - offset,
    sw_lng: coords.longitude - offset,
  };
}

/**
 * bounds를 URLSearchParams 문자열로 변환합니다.
 */
export function boundsToParams(bounds: GeoBounds): string {
  return `ne_lat=${bounds.ne_lat}&ne_lng=${bounds.ne_lng}&sw_lat=${bounds.sw_lat}&sw_lng=${bounds.sw_lng}`;
}

// ============================================
// Internal helpers
// ============================================

function getGPSLocation(): Promise<GeoCoords | null> {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: parseFloat(position.coords.latitude.toFixed(4)),
          longitude: parseFloat(position.coords.longitude.toFixed(4)),
        });
      },
      (error) => {
        reject(error);
      },
      { timeout: 5000, maximumAge: 60000 },
    );
  });
}

async function getIPLocation(): Promise<GeoCoords | null> {
  const result = await getLocationFromIP();
  if (result.latitude && result.longitude) {
    return { latitude: result.latitude, longitude: result.longitude };
  }
  return null;
}
